<script lang="ts">
  import WalletPay from './wallet-pay/WalletPay.svelte'
  import { onMount } from 'svelte'
  import { get } from 'svelte/store'
  import { stripeStyles } from '@pages/payment/stripe/Stripe.styles'
  import { dispatch, PassportEvents } from '@lib/events/events'
  import { loadScript } from '@lib/scripts/scripts'
  import { pageOptsStore, pageStore, fn } from '@packages/stores'
  import { Pages } from '@packages/utils/enums'
  import { Stripe as StripeComponent } from '@packages/ui'
  import { configStore } from '@src/stores/config'
  import { getClientConfigByClientId } from '@lib/clients'
  import type { TStripePaymentFlow } from './payment-flows'

  export let priceCents: number
  export let isLoading: boolean
  export let isPriceLoading: boolean
  export let feeToApply: number = 0
  export let paymentFlow: TStripePaymentFlow

  const { toastAlert } = get(fn)
  const config = get(configStore)

  let Stripe: Window['Stripe']
  let stripe: Window['Stripe']
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let stripeElements: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cardElement: any
  let isButtonActive: boolean

  let isCardFocused = false
  onMount(async () => {
    try {
      if (!Stripe) {
        if (!window.Stripe) {
          await loadScript('https://js.stripe.com/v3/')
          if (!window.Stripe) throw {}
        }
        Stripe = window.Stripe
      }
    } catch (err) {
      return console.log('Please include stripe to use payments.')
    }
    if (stripe) return

    const clientName = getClientConfigByClientId(config.clientId).name
    stripe = Stripe(config.stripe.pk[clientName] ?? config.stripe.pk.DEFAULT)
    stripeElements = stripe.elements()
    cardElement = stripeElements.create('card', { style: stripeStyles })
    cardElement.mount('#cpui-card-element')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cardElement.on('change', (event: any) => {
      isButtonActive = event.complete && !event.error
    })
    cardElement.on('focus', () => (isCardFocused = true))
    cardElement.on('blur', () => (isCardFocused = false))
  })

  const externalErrorHandler = (err: Error) => {
    isLoading = false
    toastAlert(`Payment error: ${err.message || err}`, 'failure')
    pageOptsStore.set({ errorMessage: err.message || err })
    pageStore.set(Pages.ERROR)
    dispatch(PassportEvents.ERROR, { error: err })
  }

  const doPayment = async (paymentMethodId?: string) => {
    try {
      isLoading = true
      const payment = await paymentFlow.createPayment()
      const result = await stripe.confirmCardPayment(payment.clientSecret, {
        payment_method: paymentMethodId || {
          card: cardElement,
        },
      })
      if (result.error) throw result.error

      await paymentFlow.afterConfirm({ context: payment.context, priceCents })
    } catch (err) {
      externalErrorHandler(err)
    } finally {
      isLoading = false
    }
  }

  const handleSubmit = async () => {
    if (!isButtonActive) return
    await doPayment()
  }

  $: totalToPay = (Math.ceil(priceCents) / 100).toFixed(2)
  $: isPaymentDisabled = !isButtonActive || isLoading || isPriceLoading
</script>

<StripeComponent
  bind:isPaymentDisabled
  bind:isLoading
  bind:totalToPay
  options={{
    isCardFocused,
    onPay: handleSubmit,
    feeToApply,
  }}
/>
{#if paymentFlow.showWalletPay !== false && stripe && stripeElements}
  <WalletPay {doPayment} {stripe} {stripeElements} {priceCents} bind:isLoading />
{/if}
